import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import OnSpotApi from '@/api/onSpot/OnSpot';
import {
    DisplayedQuestions,
    OnSpotContactData,
    OnSpotContextProps,
    OnSpotFileLists,
    OnSpotQuestions,
    OnSpotReturnTree,
} from '@/model/onSpot/OnSpot';
import Header from '@/comp/header/header';
import Footer from '@/comp/footer/Footer';
import { StepProgressBar } from '@/comp/stepProgressBar/StepProgressBar';
import './onSpot.scss';
import { OnSpotForm } from './form/onSpotForm';
import { useLocation, useNavigate } from 'react-router';
import StatusPage from '@/comp/statusPage/statusPage';
import OnSpotUpload from './upload/onSpotUpload';
import { OnSpotContact } from './contact/onSpotContact';
import { OnSpotReview } from './review/onSpotReview';
import { PageState } from '@/model/common/PageState';
import tracking from '@/_helper/tracking';
import { AppContext } from '@/context/AppContext';
import FormSkeleton from '@/comp/skeleton/form/FormSkeleton';

export const OnSpotContext = createContext<OnSpotContextProps | null>(null);

export const OnSpot = () => {
    const { bookingData } = useContext(AppContext);
    const [savedEntrypoint, setEntrypoint] = useState<Number[]>([]);
    const [dataTree, setDataTree] = useState<OnSpotQuestions>();
    const [dataReturnTree, setDataReturnTree] = useState<OnSpotReturnTree>({});
    const [displayedQuestions, setDisplayedQuestions] = useState<DisplayedQuestions>({});
    const [treeSectionId, setTreeSectionId] = useState<string>();
    const [treeSectionMapping, setTreeSectionMapping] = useState({});
    const [pageState, setPageState] = useState<PageState>(PageState.Loading);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [fileLists, setFileLists] = useState<OnSpotFileLists>({});
    const [contactData, setContactData] = useState<OnSpotContactData>({
        mail: '',
        confirmMail: '',
        mobile: {
            phoneNumber: '',
            phoneAreaCode: '',
            isLandline: false,
            phoneCountryCode: '',
            phoneCountryPrefix: '',
            lengthValid: false,
        },
    });
    const [submitData, setSubmitData] = useState({
        formValidated: false,
    });

    const navigate = useNavigate();

    const location = useLocation();

    const routes = useMemo(() => {
        return {
            root: 'hilfe-vor-ort',
            children: {
                form: '/',
                upload: 'upload',
                contact: 'kontakt',
                review: 'zusammenfassung',
            },
        };
    }, []);

    const steps = useMemo(() => {
        return [
            { label: 'Formular ausfüllen', index: 0, path: `/${routes.root}` },
            { label: 'Nachweise hochladen', index: 1, path: `/${routes.root}/${routes.children.upload}` },
            { label: 'Kontaktdaten angeben', index: 2, path: `/${routes.root}/${routes.children.contact}` },
            { label: 'Formular abschicken', index: 3, path: `/${routes.root}/${routes.children.review}` },
        ];
    }, [routes]);

    useEffect(() => {
        document.title = 'Hilfe vor Ort' + window.$documentTitle;
        tracking('loaded', 'onSpot');

        if (dataTree) {
            setTreeSectionId(dataTree['entrypoint'].id);
        }

        // restart if no data is available
        if (Object.values(dataReturnTree).length === 0) {
            navigate(`/${routes.root}`);
        }
    }, [dataReturnTree, dataTree, navigate, routes.root]);

    useEffect(() => {
        if (!bookingData?.data.allowedInteractions.onSpot) {
            navigate('/reise-hilfe-vor-ort');
            return;
        }

        const loadData = () => {
            if (!dataTree) {
                const params = new URLSearchParams({ getSubsequentTree: 'false', id: '1' });
                setPageState(PageState.Loading);
                OnSpotApi.getOnSpotTree(params).then((data) => {
                    if (data) {
                        if (data && data['entrypoint']) {
                            setPageState(PageState.Idle);
                            setDataTree(data);
                        }
                    } else {
                        setPageState(PageState.Error);
                    }
                });
            } else if (pageState !== PageState.Idle) {
                setPageState(PageState.Idle);
            }
        };

        switch (location.pathname.toLowerCase()) {
            case `/${routes.root}/`:
                navigate(`/${routes.root}`);
                break;
            case `/${routes.root}`:
                loadData();
                setCurrentStep(0);
                break;
            case `/${routes.root}/${routes.children.upload}`:
                setPageState(PageState.Idle);
                setCurrentStep(1);
                break;
            case `/${routes.root}/${routes.children.contact}`:
                // restart if no data is available
                if (Object.keys(dataReturnTree).length === 1) {
                    navigate(`/${routes.root}`);
                }

                setPageState(PageState.Idle);
                setCurrentStep(2);
                break;
            case `/${routes.root}/${routes.children.review}`:
                // navigate to contact : if no data is available
                if (
                    contactData?.mail === '' ||
                    contactData?.confirmMail === '' ||
                    contactData?.mobile?.phoneNumber === ''
                ) {
                    navigate(`/${routes.root}/${routes.children.contact}`);
                }

                if (!dataTree) {
                    loadData();
                }
                setCurrentStep(3);
                break;
            default:
                setPageState(PageState.Error);
        }
    }, [
        bookingData?.data.allowedInteractions.onSpot,
        contactData,
        currentStep,
        dataReturnTree,
        dataTree,
        location,
        navigate,
        pageState,
        routes,
    ]);

    if (!document.querySelector('html')?.classList.contains('redesign')) {
        document.querySelector('html')?.classList.add('redesign');
    }

    return (
        <>
            <div className={`page-onSpot ${submitData.formValidated ? 'form-validated' : ''}`}>
                <Header />
                {pageState === PageState.Error || pageState === PageState.Warning || pageState === PageState.Success ? (
                    <StatusPage type={pageState} subPage="onSpot" />
                ) : (
                    <main>
                        {currentStep < 4 && <StepProgressBar currentStep={currentStep} steps={steps} />}
                        {(pageState === PageState.Submitting ||
                            pageState === PageState.Loading ||
                            (!dataTree && pageState === PageState.Idle)) && <FormSkeleton />}
                        {currentStep >= 0 && pageState === PageState.Idle && dataTree && treeSectionId && (
                            <OnSpotContext.Provider
                                value={{
                                    routes,
                                    pageState,
                                    setPageState,
                                    dataTree,
                                    setDataTree,
                                    savedEntrypoint,
                                    setEntrypoint,
                                    displayedQuestions,
                                    setDisplayedQuestions,
                                    dataReturnTree,
                                    setDataReturnTree,
                                    treeSectionId,
                                    setTreeSectionId,
                                    treeSectionMapping,
                                    setTreeSectionMapping,
                                    fileLists,
                                    setFileLists,
                                    contactData,
                                }}>
                                {currentStep === 0 ? (
                                    <OnSpotForm
                                        saveEntrypoint={setEntrypoint}
                                        setDisplayedQuestions={setDisplayedQuestions}
                                    />
                                ) : currentStep === 1 ? (
                                    <OnSpotUpload />
                                ) : currentStep === 2 ? (
                                    <OnSpotContact
                                        contactData={contactData}
                                        setContactData={(mail, confirmMail, mobile) =>
                                            setContactData({
                                                mail: mail,
                                                confirmMail: confirmMail,
                                                mobile: mobile,
                                            })
                                        }
                                    />
                                ) : currentStep === 3 ? (
                                    dataTree && (
                                        <OnSpotReview
                                            submitData={setSubmitData}
                                            treeSectionMapping={treeSectionMapping}
                                            data={dataTree}
                                            returnTree={dataReturnTree}
                                        />
                                    )
                                ) : (
                                    <>success oder error</>
                                )}
                            </OnSpotContext.Provider>
                        )}
                    </main>
                )}
                <Footer />
            </div>
        </>
    );
};
export default OnSpot;
